/**
 * Portal PÚBLICO de la cotización (/cotizacion/:token), sin login. V142.
 * El cliente ve conceptos/unidades/descuentos/total/vigencia y puede ACEPTAR o
 * RECHAZAR. Optimistic lock: se manda la versión en pantalla; si cambió, el
 * server responde 409 y se pide recargar (protege de aceptar un precio no visto).
 * Token revocado/regenerado o inexistente → 404 (fail-closed). Turnstile en las
 * acciones (fail-open sin site key). Patrón SelfInvoicePage: pathname + fetch crudo.
 */
import { useEffect, useState } from 'react';
import { API_BASE } from '../lib/api';
import { TurnstileWidget } from '../components/TurnstileWidget';

const PATH_RE = /^\/cotizacion\/([0-9a-f]{48})\/?$/i;
export const isQuotePortalRoute = (pathname: string): boolean => PATH_RE.test(pathname);

async function publicJson<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' }, ...opts,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw { status: res.status, body, message: body?.message };
  return body as T;
}

const money = (n: any, ccy: string) =>
  `${ccy} ${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (s?: string | null) => (s ? String(s).slice(0, 10) : '—');
const UNIT: Record<string, string> = { hora: 'h', dia: 'día', mes: 'mes', entregable: 'entregable', pieza: 'pza' };

export function QuotePortalPage() {
  const token = window.location.pathname.match(PATH_RE)?.[1] ?? '';
  const [data, setData] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const [action, setAction] = useState<'idle' | 'accepting' | 'rejecting'>('idle');
  const [signer, setSigner] = useState({ name: '', email: '', terms: false });
  const [reason, setReason] = useState('');
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [stale, setStale] = useState(false);  // #4: la versión cambió mientras el cliente miraba
  const [done, setDone] = useState<null | { kind: 'accepted' | 'rejected' }>(null);

  const load = () => {
    publicJson<any>(`/public/quotes/${token}`).then(r => { setData(r); setErr(null); }).catch(() => setNotFound(true));
  };
  useEffect(load, [token]);

  const submit = async (kind: 'accept' | 'reject') => {
    setBusy(true); setErr(null);
    try {
      const base = { expectedVersion: data.version, ...(tsToken ? { turnstileToken: tsToken } : {}) };
      if (kind === 'accept') {
        if (!signer.name.trim() || !signer.terms) { setErr('Escribe tu nombre y acepta los términos.'); setBusy(false); return; }
        await publicJson(`/public/quotes/${token}/accept`, {
          method: 'POST',
          body: JSON.stringify({ ...base, signerName: signer.name.trim(), signerEmail: signer.email.trim() || undefined, acceptedTerms: true }),
        });
        setDone({ kind: 'accepted' });
      } else {
        await publicJson(`/public/quotes/${token}/reject`, {
          method: 'POST', body: JSON.stringify({ ...base, reason: reason.trim() || undefined }),
        });
        setDone({ kind: 'rejected' });
      }
    } catch (e: any) {
      if (e?.status === 409 && e?.body?.code === 'stale_version') {
        // #4: NO se aceptó. Se recarga la versión nueva y se avisa PROMINENTE antes
        // de que el cliente vuelva a presionar Aceptar (el precio pudo cambiar).
        setStale(true); setAction('idle'); setErr(null); load();
      } else if (e?.status === 404 || e?.status === 410) {
        setErr(e?.body?.message || 'Esta cotización ya no está disponible.');
      } else {
        setErr(e?.body?.message || e?.message || 'No se pudo procesar. Intenta de nuevo.');
      }
    } finally { setBusy(false); }
  };

  const shell = (children: any) => (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="mx-auto max-w-2xl">{children}</div>
    </div>
  );

  if (notFound) return shell(
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <h1 className="font-serif text-2xl text-slate-900">Cotización no disponible</h1>
      <p className="mt-2 text-sm text-slate-500">El enlace no es válido o fue revocado. Solicita uno nuevo al negocio.</p>
    </div>
  );
  if (!data) return shell(<div className="h-64 animate-pulse rounded-2xl bg-slate-100" />);

  if (done) return shell(
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <div className={`mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full ${done.kind === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
        {done.kind === 'accepted' ? '✓' : '—'}
      </div>
      <h1 className="font-serif text-2xl text-slate-900">
        {done.kind === 'accepted' ? '¡Cotización aceptada!' : 'Cotización rechazada'}
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        {done.kind === 'accepted'
          ? `Gracias. El negocio ya fue notificado y comenzará el proyecto (${data.quote.code}).`
          : 'Registramos tu respuesta. Gracias por tu tiempo.'}
      </p>
    </div>
  );

  const q = data.quote;
  const isSent = data.status === 'SENT';
  const lines = data.lines || [];

  return shell(
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-900 px-6 py-5 text-white">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/60">Cotización {q.code}</p>
        <h1 className="mt-1 font-serif text-2xl">{q.title}</h1>
        <p className="mt-1 text-xs text-white/70">Vigencia: {fmtDay(q.valid_until)} · v{data.version}</p>
      </div>

      {!isSent && (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-900">
          {data.status === 'ACCEPTED' ? 'Esta cotización ya fue aceptada.'
            : data.status === 'REJECTED' ? 'Esta cotización fue rechazada.'
            : 'Esta cotización se está actualizando. Vuelve pronto para revisar la versión más reciente.'}
        </div>
      )}

      <div className="px-6 py-5">
        {stale && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span className="text-lg leading-none">⚠️</span>
            <p><strong>Esta cotización se actualizó</strong>{q.updated_at ? ` el ${fmtDay(q.updated_at)}` : ''}. Revisa los cambios abajo antes de aceptar — el precio o los conceptos pudieron cambiar.</p>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
            <tr><th className="py-1.5 text-left font-medium">Concepto</th><th className="text-right font-medium">Cant.</th><th className="text-right font-medium">P. unit.</th><th className="text-right font-medium">Importe</th></tr>
          </thead>
          <tbody>
            {lines.map((l: any, i: number) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-2 text-slate-800">{l.description}</td>
                <td className="text-right tabular-nums text-slate-600">{Number(l.quantity)} {UNIT[l.sale_unit] || ''}</td>
                <td className="text-right tabular-nums text-slate-600">{money(l.unit_price, q.currency)}</td>
                <td className="text-right tabular-nums text-slate-800">{money(l.line_total, q.currency)}
                  {Number(l.line_discount) > 0 && <div className="text-[10px] text-amber-700">−{money(l.line_discount, q.currency)} desc.</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="tabular-nums">{money(q.subtotal, q.currency)}</span></div>
          {Number(q.global_discount) > 0 && <div className="flex justify-between"><span className="text-slate-600">Descuento</span><span className="tabular-nums text-amber-700">−{money(q.global_discount, q.currency)}</span></div>}
          <div className="flex justify-between"><span className="text-slate-600">IVA ({(Number(q.tax_rate) * 100).toFixed(2).replace(/\.00$/, '')}%)</span><span className="tabular-nums">{money(q.tax_amount, q.currency)}</span></div>
          <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-semibold"><span>Total</span><span className="tabular-nums">{money(q.total_amount, q.currency)}</span></div>
        </div>

        {q.notes && <p className="mt-4 whitespace-pre-line text-xs text-slate-500"><strong>Notas:</strong> {q.notes}</p>}
        {q.terms && <p className="mt-2 whitespace-pre-line text-xs text-slate-500"><strong>Términos:</strong> {q.terms}</p>}

        {err && <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}

        {isSent && action === 'idle' && (
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button onClick={() => setAction('rejecting')} className="rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">Rechazar</button>
            <button onClick={() => setAction('accepting')} className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">Aceptar cotización</button>
          </div>
        )}

        {isSent && action === 'accepting' && (
          <div className="mt-6 space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <input className="input" placeholder="Tu nombre completo" value={signer.name} onChange={e => setSigner(s => ({ ...s, name: e.target.value }))} />
            <input className="input" placeholder="Tu correo (opcional)" value={signer.email} onChange={e => setSigner(s => ({ ...s, email: e.target.value }))} />
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={signer.terms} onChange={e => setSigner(s => ({ ...s, terms: e.target.checked }))} />
              Acepto los términos de esta cotización.
            </label>
            <TurnstileWidget onToken={setTsToken} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setAction('idle')} disabled={busy} className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">Cancelar</button>
              <button onClick={() => submit('accept')} disabled={busy} className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">{busy ? 'Enviando…' : 'Confirmar aceptación'}</button>
            </div>
          </div>
        )}

        {isSent && action === 'rejecting' && (
          <div className="mt-6 space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <textarea className="input" rows={2} placeholder="Motivo (opcional)" value={reason} onChange={e => setReason(e.target.value)} />
            <TurnstileWidget onToken={setTsToken} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setAction('idle')} disabled={busy} className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">Cancelar</button>
              <button onClick={() => submit('reject')} disabled={busy} className="rounded-lg bg-slate-700 px-4 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50">{busy ? 'Enviando…' : 'Confirmar rechazo'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
